<template>
  <TWModal
    :title="$t('users.userDetails')"
    :show="show"
    :loading="false"
    @close="handleClose"
    :allow-outside-click-to-close="false"
  >
    <template v-slot:content>
      <div class="min-h-tabModalContent">
        <TabList
          :tabs="tabs"
          v-model="selected"
        />
        <TabGroup
          :tabs="tabs"
          :selected="selected"
        >
          <template v-slot:general>
            <div data-test="user-general-tab">
              <form>
                <div>
                  <div class="mb-4">
                    <label class="mb-1 inline-block">{{ $t('users.name') }}</label>
                    <p>{{ user.name }}</p>
                  </div>
                  <div class="mb-4">
                    <label class="mt-sm mb-1 inline-block">{{ $t('users.email') }}</label>
                    <p>{{ user.email }}</p>
                  </div>
                  <div class="mb-4">
                    <label class="mt-sm mb-1 inline-block">{{ $t('users.role') }}</label>
                    <p>{{ $t(`users.roles.${user.role}`) }}</p>
                  </div>
                  <div class="mb-2">
                    <label class="mt-sm mb-1 inline-block">{{ $t('users.teams') }}</label>
                    <p>
                      <span>{{ formatUserGroups(user.groups) }}</span>
                    </p>
                  </div>
                  <div class="mb-2">
                    <label class="mt-sm mb-1 inline-block">{{ $t('functional.created') }}</label>
                    <p>{{ user.created_at | formatDate('timestamp') }}</p>
                  </div>
                </div>
              </form>
            </div>
          </template>
          <template v-slot:security>
            <div
              v-if="userHasRole('super-admin')"
              data-test="user-security-tab"
            >
              <UserMfa :user="selectedUser" />
              <label class="mb-2 block mt-base">{{ $t('users.password')}}</label>
              <BaseButton
                @click.native="handleOpeningResetPasswordModal"
                id="reset-user-password-button"
                data-ec="view_user_modal"
                data-et="btn_open_password_reset_modal"
              >
                {{ $t('users.resetPassword') }}
              </BaseButton>
            </div>
            <div
              v-else
              class="flex items-center gap-base"
            >
              <InfoIndicator />
              <span>{{ $t('users.contactAdminForSecurity') }}</span>
            </div>
          </template>
        </TabGroup>
      </div>
    </template>
    <template v-slot:controls>
      <div class="w-full md:flex md:justify-between">
        <div class="flex flex-col md:flex-row gap-base">
          <BaseButton
            variant="danger"
            id="delete-user-button"
            @click.native="handleOpeningDeleteUserModal"
            data-ec="view_user_modal"
            data-et="btn_open_delete_modal"
          >
            {{ $t('users.deleteUser') }}
          </BaseButton>
          <BaseButton
            @click.native="handleOpeningEditUserModal"
            variant="secondary"
            id="edit-user-button"
            data-ec="view_user_modal"
            data-et="btn_open_edit_user_modal"
          >
            {{ $t('users.edit') }}
          </BaseButton>
        </div>
        <div class="flex flex-col mt-base md:flex-row md:mt-0 gap-base md:justify-items-end md:items-end">
          <BaseButton
            @click.native="handleCloseButtonClicked"
            id="close-button"
            data-ec="view_user_modal"
            data-et="btn_close"
          >
            {{ $t('functional.close') }}
          </BaseButton>
        </div>
      </div>
    </template>
  </TWModal>
</template>

<script>
import { mapGetters } from 'vuex';

import BaseButton from '@/components/library/buttons/BaseButton.vue';
import InfoIndicator from '@/components/library/indicators/InfoIndicator.vue';
import { TabGroup } from '@/components/library/tabs/TabGroup';
import { TabList } from '@/components/library/tabs/TabList';
import TWModal from '@/components/tailwind/Modal.vue';
import UserMfa from '@/components/UserMfa.vue';
import formatUserGroupsMixin from '@/mixins/formatUserGroups';

export default {
  name: 'ViewUserModal',
  props: {
    selectedUser: {
      type: Object,
      required: true,
    },
    show: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [formatUserGroupsMixin],
  components: {
    UserMfa,
    TabGroup,
    TabList,
    InfoIndicator,
    BaseButton,
    TWModal,
  },
  data() {
    return {
      user: { ...this.selectedUser },
      selected: 'general',
      tabs: [
        {
          title: this.$t('users.general'),
          id: 'general',
        },
        {
          title: this.$t('users.security'),
          id: 'security',
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      authenticatedUser: 'user/user',
      featureEnabled: 'featureFlags/featureEnabled',
      userHasRole: 'roles/userHasRole',
    }),
  },
  watch: {
    selectedUser: {
      deep: true,
      handler(newVal) {
        this.user = { ...newVal };
      },
    },
  },
  methods: {
    handleCloseButtonClicked() {
      this.handleClose();
    },
    handleClose() {
      // Reset the component to the initial state when created
      Object.assign(this.$data, this.$options.data.apply(this));
      this.$emit('close');
    },
    handleOpeningDeleteUserModal() {
      this.handleClose();
      this.$emit('delete-user');
    },
    handleOpeningResetPasswordModal() {
      this.handleClose();
      this.$emit('reset-user-password');
    },
    handleOpeningEditUserModal() {
      this.handleClose();
      this.$emit('edit-user');
    },
  },
};
</script>

<style scoped>

</style>
